import React, { useState } from 'react';
import { usePropertyPanel } from '../PropertyPanelContext';
import { useSelector, useDispatch } from 'react-redux';
import { selectSelectedComponent, updateComponent } from '../../../redux/editorSlice';
import { PaddingControl, BorderRadiusControl, StyleControl, SkinSelector } from '../CommonProperties';
import axios from 'axios';

const MainBannerProperties = () => {
  // 선택된 컴포넌트 가져오기
  const selectedComponent = useSelector(selectSelectedComponent);
  const dispatch = useDispatch();
  
  // 컨텍스트에서 필요한 값과 함수들 가져오기
  const {
    devicePropertyTab,
    handleImageUpload,
    getDevicePropertyValue
  } = usePropertyPanel();
  
  // 배너 편집 모달 상태
  const [showBannerEdit, setShowBannerEdit] = useState(false);
  const [bannerEditData, setBannerEditData] = useState({
    index: -1,
    title: '',
    description: '',
    url: '#',
    icon: '',
    mediaType: 'image',
    videoUrl: '',
    autoplay: true,
    muted: true,
    loop: true,
    position: '5',
    hasBackground: false,
    textColor: 'white',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.6)',
    buttonBgColor: '#ff6b6b',
    buttonTextColor: 'white',
    buttonHoverColor: '#ff5252',
    buttonText: '자세히 보기',
    transparentButton: false,
    buttonWidth: 'auto',
    buttonHeight: 'auto'
  });
  
  // 컴포넌트 속성 변경 핸들러
  const handleComponentPropsChange = (property, value) => {
    if (!selectedComponent) return;
    
    const existingProps = JSON.parse(JSON.stringify(selectedComponent.componentProps || {}));
    
    // 디바이스별 속성 처리
    if (devicePropertyTab) {
      if (!existingProps.deviceProperty) {
        existingProps.deviceProperty = { pc: {}, mobile: {} };
        
        // 기존 속성 복사
        Object.keys(existingProps).forEach(key => {
          if (key !== 'deviceProperty') {
            existingProps.deviceProperty.pc[key] = existingProps[key];
            existingProps.deviceProperty.mobile[key] = existingProps[key];
          }
        });
      }
      
      if (!existingProps.deviceProperty[devicePropertyTab]) {
        existingProps.deviceProperty[devicePropertyTab] = {};
      }
      
      existingProps.deviceProperty[devicePropertyTab][property] = value;
      
      // PC 모드일 때만 최상위 속성에도 적용
      if (devicePropertyTab === 'pc') {
        existingProps[property] = value;
      }
    } else {
      existingProps[property] = value;
      
      // 디바이스별 속성도 같이 업데이트
      if (existingProps.deviceProperty) {
        if (existingProps.deviceProperty.pc) {
          existingProps.deviceProperty.pc[property] = value;
        }
        if (existingProps.deviceProperty.mobile) {
          existingProps.deviceProperty.mobile[property] = value;
        }
      }
    }
    
    // Redux 상태 업데이트
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        componentProps: existingProps
      }
    }));
  };
  
  // 배너 이미지 업로드 핸들러 - 직접 구현으로 변경
  const handleBannerImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // 이미지 파일 타입 체크
    if (!file.type.startsWith('image/')) {
      alert('이미지 파일만 업로드 가능합니다.');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bucket', 'withcookie-bucket');
      formData.append('path', 'c/default');
      
      const res = await axios.post(
        `https://refrigerator.logipasta.com/v1/file`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Access-Control-Allow-Origin': '*',
          },
          timeout: 30000,
        }
      );
      
      if (res.data && res.data.file) {
        alert('이미지가 성공적으로 업로드되었습니다.');
        setBannerEditData({...bannerEditData, icon: res.data.file});
      } else {
        console.error('이미지 URL이 응답에 없습니다:', res.data);
        alert('서버 응답에 이미지 URL이 포함되어 있지 않습니다. 관리자에게 문의하세요.');
      }
    } catch (err) {
      console.error('이미지 업로드 실패:', err);
      
      let errorMessage = '이미지 업로드에 실패했습니다. ';
      
      if (err.response) {
        console.error('서버 응답 오류:', err.response.status, err.response.data);
        errorMessage += `서버 응답 오류 (${err.response.status}): ${JSON.stringify(err.response.data)}`;
      } else if (err.request) {
        console.error('서버 응답 없음:', err.request);
        errorMessage += '서버로부터 응답이 없습니다. 네트워크 연결을 확인해주세요.';
      } else if (err.code === 'ECONNABORTED') {
        errorMessage += '요청 시간이 초과되었습니다. 나중에 다시 시도해주세요.';
      } else {
        console.error('요청 오류:', err.message);
        errorMessage += err.message;
      }
      
      alert(errorMessage);
    }
  };
  
  // 배너 비디오 업로드 핸들러 - 직접 구현으로 변경
  const handleBannerVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // 비디오 파일 타입 체크
    if (!file.type.startsWith('video/')) {
      alert('비디오 파일만 업로드 가능합니다.');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bucket', 'withcookie-bucket');
      formData.append('path', 'c/default');
      
      const res = await axios.post(
        `https://refrigerator.logipasta.com/v1/file`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Access-Control-Allow-Origin': '*',
          },
          timeout: 30000,
        }
      );
      
      if (res.data && res.data.file) {
        alert('비디오가 성공적으로 업로드되었습니다.');
        setBannerEditData({...bannerEditData, videoUrl: res.data.file});
      } else {
        console.error('비디오 URL이 응답에 없습니다:', res.data);
        alert('서버 응답에 비디오 URL이 포함되어 있지 않습니다. 관리자에게 문의하세요.');
      }
    } catch (err) {
      console.error('비디오 업로드 실패:', err);
      
      let errorMessage = '비디오 업로드에 실패했습니다. ';
      
      if (err.response) {
        console.error('서버 응답 오류:', err.response.status, err.response.data);
        errorMessage += `서버 응답 오류 (${err.response.status}): ${JSON.stringify(err.response.data)}`;
      } else if (err.request) {
        console.error('서버 응답 없음:', err.request);
        errorMessage += '서버로부터 응답이 없습니다. 네트워크 연결을 확인해주세요.';
      } else if (err.code === 'ECONNABORTED') {
        errorMessage += '요청 시간이 초과되었습니다. 나중에 다시 시도해주세요.';
      } else {
        console.error('요청 오류:', err.message);
        errorMessage += err.message;
      }
      
      alert(errorMessage);
    }
  };
  
  // 배너 저장 핸들러
  const handleSaveBanner = () => {
    if (!selectedComponent) return;
    
    const existingProps = JSON.parse(JSON.stringify(selectedComponent.componentProps || {}));
    
    // 편집 중인 배너 데이터
    const bannerData = {
      text: bannerEditData.title,
      description: bannerEditData.description,
      url: bannerEditData.url,
      icon: bannerEditData.icon,
      mediaType: bannerEditData.mediaType,
      videoUrl: bannerEditData.videoUrl,
      autoplay: bannerEditData.autoplay,
      muted: bannerEditData.muted,
      loop: bannerEditData.loop,
      position: bannerEditData.position,
      hasBackground: bannerEditData.hasBackground,
      textColor: bannerEditData.textColor,
      textShadow: bannerEditData.textShadow,
      buttonBgColor: bannerEditData.buttonBgColor,
      buttonTextColor: bannerEditData.buttonTextColor,
      buttonHoverColor: bannerEditData.buttonHoverColor,
      buttonText: bannerEditData.buttonText,
      transparentButton: bannerEditData.transparentButton,
      buttonWidth: bannerEditData.buttonWidth,
      buttonHeight: bannerEditData.buttonHeight
    };
    
    // 디바이스별 속성 처리
    if (devicePropertyTab) {
      if (!existingProps.deviceProperty) {
        existingProps.deviceProperty = { pc: {}, mobile: {} };
      }
      
      if (!existingProps.deviceProperty[devicePropertyTab]) {
        existingProps.deviceProperty[devicePropertyTab] = {};
      }
      
      // 기존 배너 배열 가져오기
      let banners = existingProps.deviceProperty[devicePropertyTab].banners || [];
      
      if (bannerEditData.index >= 0) {
        // 기존 배너 수정
        banners[bannerEditData.index] = bannerData;
      } else {
        // 새 배너 추가
        banners = [...banners, bannerData];
      }
      
      existingProps.deviceProperty[devicePropertyTab].banners = banners;
      
      // PC 모드일 때만 최상위 속성에도 적용
      if (devicePropertyTab === 'pc') {
        existingProps.banners = banners;
      }
    } else {
      // 디바이스 속성 탭이 선택되지 않은 경우
      let banners = existingProps.banners || [];
      
      if (bannerEditData.index >= 0) {
        banners[bannerEditData.index] = bannerData;
      } else {
        banners = [...banners, bannerData];
      }
      
      existingProps.banners = banners;
      
      // 디바이스별 속성도 같이 업데이트
      if (existingProps.deviceProperty) {
        if (existingProps.deviceProperty.pc) {
          existingProps.deviceProperty.pc.banners = banners;
        }
        if (existingProps.deviceProperty.mobile) {
          existingProps.deviceProperty.mobile.banners = banners;
        }
      }
    }
    
    // 컴포넌트 업데이트
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        componentProps: existingProps
      }
    }));
    
    // 모달 닫기
    setShowBannerEdit(false);
  };
  
  // 위치에 따른 라벨 표시
  const getPosLabel = (pos) => {
    const positions = {
      '1': '좌측상단',
      '2': '중앙상단',
      '3': '우측상단',
      '4': '좌측중앙',
      '5': '중앙',
      '6': '우측중앙',
      '7': '좌측하단',
      '8': '중앙하단',
      '9': '우측하단'
    };
    
    return positions[pos] || '중앙';
  };
  
  return (
    <>
      <SkinSelector 
        selectedComponent={selectedComponent}
        onSkinChange={(skinId) => handleComponentPropsChange('skin', skinId)}
      />
      
      <div className="property-group">
        <div className="property-group-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
            <path d="M7 7H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M7 12H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M7 17H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          배너 설정
        </div>
        
        {/* 디바이스별 배너 데이터 선택 UI */}
        <div className="device-specific-notice" style={{
          padding: '8px',
          marginBottom: '10px',
          backgroundColor: '#f0f9ff',
          borderRadius: '4px',
          fontSize: '14px',
          color: '#0369a1'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
            {devicePropertyTab === 'pc' ? 'PC용 배너' : '모바일/태블릿용 배너'} 설정 중
          </div>
          <div>
            현재 {devicePropertyTab === 'pc' ? 'PC' : '모바일/태블릿'} 환경에서 보여지는 배너를 설정하고 있습니다.
            다른 디바이스 환경의 배너를 설정하려면 상단의 디바이스 탭을 변경하세요.
          </div>
        </div>
        
        {/* <div className="property-control">
          <label>배너 모드</label>
          <select
            value={getDevicePropertyValue(selectedComponent.componentProps, 'mode', 'slider')}
            onChange={(e) => handleComponentPropsChange('mode', e.target.value)}
          >
            <option value="slider">슬라이더 (자동 전환)</option>
            <option value="list">목록 (가로 스크롤)</option>
          </select>
        </div> */}
        
        {/* 배너 목록 관리 */}
        <div className="banner-list-management">
          <h4>배너 목록</h4>
          
          {/* 현재 디바이스 탭에 따라 배너 목록 표시 */}
          {(() => {
            // 디바이스별 배너 가져오기
            let banners = [];
            if (devicePropertyTab && 
                selectedComponent.componentProps?.deviceProperty && 
                selectedComponent.componentProps.deviceProperty[devicePropertyTab]?.banners) {
              banners = selectedComponent.componentProps.deviceProperty[devicePropertyTab].banners;
            } else if (selectedComponent.componentProps?.banners) {
              banners = selectedComponent.componentProps.banners;
            }
            
            return banners.map((banner, index) => (
              <div key={index} className="banner-item" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px',
                marginBottom: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: banner.mediaType === 'video' ? '#f0f9ff' : '#f9fafb'
              }}>
                <div className="banner-preview" style={{ display: 'flex', alignItems: 'center' }}>
                  <div className="banner-thumbnail" style={{ marginRight: '10px' }}>
                    {banner.mediaType === 'video' ? (
                      <div style={{ 
                        width: '40px', 
                        height: '40px', 
                        backgroundColor: '#0ea5e9',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white'
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M5 5h14c1.1 0 2 .9 2 2v10c0 1.1-.9 2-2 2H5c-1.1 0-2-.9-2-2V7c0-1.1.9-2 2-2z" stroke="white" strokeWidth="2" />
                          <path d="M10 9l5 3-5 3V9z" fill="white" />
                        </svg>
                      </div>
                    ) : (
                      <img 
                        src={banner.icon || 'https://via.placeholder.com/40'} 
                        alt={banner.text || `배너 ${index + 1}`} 
                        style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} 
                      />
                    )}
                  </div>
                  <div className="banner-info">
                    <div style={{ fontWeight: 'bold' }}>{banner.text || `배너 ${index + 1}`}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {banner.mediaType === 'video' ? '비디오 배너' : '이미지 배너'}
                    </div>
                  </div>
                </div>
                <div className="banner-actions">
                  <button 
                    onClick={() => {
                      setBannerEditData({
                        index,
                        title: banner.text || '',
                        description: banner.description || '',
                        url: banner.url || '',
                        icon: banner.icon || '',
                        mediaType: banner.mediaType || 'image',
                        videoUrl: banner.videoUrl || '',
                        autoplay: banner.autoplay !== false,
                        muted: banner.muted !== false,
                        loop: banner.loop !== false,
                        position: banner.position || '5',
                        hasBackground: banner.hasBackground || false,
                        textColor: banner.textColor || 'white',
                        textShadow: banner.textShadow || '0 1px 2px rgba(0, 0, 0, 0.6)',
                        buttonBgColor: banner.buttonBgColor || '#ff6b6b',
                        buttonTextColor: banner.buttonTextColor || 'white',
                        buttonHoverColor: banner.buttonHoverColor || '#ff5252',
                        buttonText: banner.buttonText || '자세히 보기',
                        transparentButton: banner.transparentButton || false,
                        buttonWidth: banner.buttonWidth || 'auto',
                        buttonHeight: banner.buttonHeight || 'auto'
                      });
                      setShowBannerEdit(true);
                    }}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: '#4b5563',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      marginRight: '4px',
                      fontSize: '12px'
                    }}
                  >
                    편집
                  </button>
                  <button 
                    onClick={() => {
                      // 삭제 확인
                      if (window.confirm(`정말로 배너 #${index + 1}을(를) 삭제하시겠습니까?`)) {
                        // 현재 디바이스 탭의 배너만 수정
                        const existingProps = selectedComponent.componentProps ? 
                          JSON.parse(JSON.stringify(selectedComponent.componentProps)) : {};
                        
                        if (devicePropertyTab && existingProps.deviceProperty) {
                          // 디바이스별 배너 삭제
                          const deviceBanners = existingProps.deviceProperty[devicePropertyTab]?.banners || [];
                          const updatedBanners = deviceBanners.filter((_, i) => i !== index);
                          
                          if (!existingProps.deviceProperty[devicePropertyTab]) {
                            existingProps.deviceProperty[devicePropertyTab] = {};
                          }
                          existingProps.deviceProperty[devicePropertyTab].banners = updatedBanners;
                          
                          // PC 모드일 경우 최상위 배너 배열도 업데이트
                          if (devicePropertyTab === 'pc') {
                            existingProps.banners = updatedBanners;
                          }
                        } else {
                          // 모든 디바이스 배너 삭제
                          const banners = existingProps.banners || [];
                          const updatedBanners = banners.filter((_, i) => i !== index);
                          existingProps.banners = updatedBanners;
                          
                          // deviceProperty가 있으면 모든 디바이스에 적용
                          if (existingProps.deviceProperty) {
                            if (existingProps.deviceProperty.pc) {
                              existingProps.deviceProperty.pc.banners = updatedBanners;
                            }
                            if (existingProps.deviceProperty.mobile) {
                              existingProps.deviceProperty.mobile.banners = updatedBanners;
                            }
                          }
                        }
                        
                        // 컴포넌트 업데이트
                        dispatch(updateComponent({
                          id: selectedComponent.id,
                          changes: {
                            componentProps: existingProps
                          }
                        }));
                      }
                    }}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    삭제
                  </button>
                </div>
              </div>
            ));
          })()}
          
          {/* 새 배너 추가 버튼 */}
          <button 
            onClick={() => {
              setBannerEditData({
                index: -1,
                title: '',
                description: '',
                url: '#',
                icon: '',
                mediaType: 'image',
                videoUrl: '',
                autoplay: true,
                muted: true,
                loop: true,
                position: '5',
                hasBackground: false,
                textColor: 'white',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.6)',
                buttonBgColor: '#ff6b6b',
                buttonTextColor: 'white',
                buttonHoverColor: '#ff5252',
                buttonText: '자세히 보기',
                transparentButton: false,
                buttonWidth: 'auto',
                buttonHeight: 'auto'
              });
              setShowBannerEdit(true);
            }}
            style={{
              padding: '8px 12px',
              backgroundColor: '#0ea5e9',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              width: '100%',
              marginTop: '10px'
            }}
          >
            새 배너 추가
          </button>
        </div>
      </div>
      
      {/* 배너 편집 모달 */}
      {showBannerEdit && (
        <div className="banner-edit-modal" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div className="modal-header" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <h3 style={{ margin: 0 }}>
                {bannerEditData.index >= 0 ? '배너 편집' : '새 배너 추가'}
              </h3>
              <button 
                onClick={() => setShowBannerEdit(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  marginLeft: 'auto'
                }}
              >
                &times;
              </button>
            </div>
            
            <div className="modal-body">
              {/* 미디어 타입 선택 UI */}
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>미디어 타입</label>
                <div className="media-type-selector" style={{ display: 'flex', gap: '10px' }}>
                  <button 
                    onClick={() => setBannerEditData({...bannerEditData, mediaType: 'image'})}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: bannerEditData.mediaType === 'image' ? '2px solid #0ea5e9' : '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: bannerEditData.mediaType === 'image' ? '#f0f9ff' : 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>이미지</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>정적 이미지 배너</div>
                  </button>
                  <button 
                    onClick={() => setBannerEditData({...bannerEditData, mediaType: 'video'})}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: bannerEditData.mediaType === 'video' ? '2px solid #0ea5e9' : '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: bannerEditData.mediaType === 'video' ? '#f0f9ff' : 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>비디오</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>동영상 배너</div>
                  </button>
                </div>
              </div>
              
              {/* 미디어 타입에 따라 다른 UI 표시 */}
              {bannerEditData.mediaType === 'image' ? (
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>배너 이미지</label>
                  <div className="image-url-container" style={{ 
                    display: 'flex', 
                    gap: '8px', 
                    marginBottom: '8px' 
                  }}>
                    <input 
                      type="text" 
                      value={bannerEditData.icon}
                      onChange={(e) => setBannerEditData({...bannerEditData, icon: e.target.value})}
                      placeholder="이미지 URL을 입력하세요"
                      style={{
                        flex: 1,
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                    />
                    <label style={{
                      padding: '8px 12px',
                      backgroundColor: '#4b5563',
                      color: 'white',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}>
                      업로드
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={handleBannerImageUpload}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </div>
                  {bannerEditData.icon && (
                    <div className="image-preview" style={{ 
                      marginTop: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '8px',
                      backgroundColor: '#f9fafb'
                    }}>
                      <img 
                        src={bannerEditData.icon} 
                        alt="배너 이미지 미리보기" 
                        style={{ 
                          width: '100%',
                          maxHeight: '200px',
                          objectFit: 'contain'
                        }} 
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>비디오 URL</label>
                  <div className="video-url-container" style={{ 
                    display: 'flex', 
                    gap: '8px', 
                    marginBottom: '8px' 
                  }}>
                    <input 
                      type="text" 
                      value={bannerEditData.videoUrl}
                      onChange={(e) => setBannerEditData({...bannerEditData, videoUrl: e.target.value})}
                      placeholder="비디오 URL을 입력하세요"
                      style={{
                        flex: 1,
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                    />
                    <label style={{
                      padding: '8px 12px',
                      backgroundColor: '#4b5563',
                      color: 'white',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}>
                      업로드
                      <input 
                        type="file" 
                        accept="video/*" 
                        onChange={handleBannerVideoUpload}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </div>
                  
                  {/* 비디오 설정 옵션 추가 */}
                  <div className="video-options" style={{ marginTop: '16px' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '12px' }}>비디오 설정</div>
                    
                    {/* 자동 재생 설정 - 유지 */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '10px 12px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '4px',
                      marginBottom: '8px'
                    }}>
                      <div>
                        <div style={{ fontWeight: '500' }}>자동 재생</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                          배너가 표시될 때 비디오가 자동으로 재생됩니다
                        </div>
                      </div>
                      <label style={{ 
                        position: 'relative',
                        display: 'inline-block',
                        width: '44px',
                        height: '24px'
                      }}>
                        <input
                          type="checkbox"
                          checked={bannerEditData.autoplay}
                          onChange={(e) => setBannerEditData({...bannerEditData, autoplay: e.target.checked})}
                          style={{ 
                            opacity: 0,
                            width: 0,
                            height: 0
                          }}
                        />
                        <span style={{
                          position: 'absolute',
                          cursor: 'pointer',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: bannerEditData.autoplay ? '#0ea5e9' : '#cbd5e1',
                          transition: '.3s',
                          borderRadius: '24px',
                        }}>
                          <span style={{
                            position: 'absolute',
                            content: '""',
                            height: '16px',
                            width: '16px',
                            left: bannerEditData.autoplay ? '24px' : '4px',
                            bottom: '4px',
                            backgroundColor: 'white',
                            transition: '.3s',
                            borderRadius: '50%'
                          }} />
                        </span>
                      </label>
                    </div>
                    
                    {/* 소리 재생 설정 - 추가 */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '10px 12px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '4px',
                      marginBottom: '8px'
                    }}>
                      <div>
                        <div style={{ fontWeight: '500' }}>소리 음소거</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                          비활성화 시 비디오가 소리와 함께 재생됩니다
                        </div>
                      </div>
                      <label style={{ 
                        position: 'relative',
                        display: 'inline-block',
                        width: '44px',
                        height: '24px'
                      }}>
                        <input
                          type="checkbox"
                          checked={bannerEditData.muted}
                          onChange={(e) => setBannerEditData({...bannerEditData, muted: e.target.checked})}
                          style={{ 
                            opacity: 0,
                            width: 0,
                            height: 0
                          }}
                        />
                        <span style={{
                          position: 'absolute',
                          cursor: 'pointer',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: bannerEditData.muted ? '#0ea5e9' : '#cbd5e1',
                          transition: '.3s',
                          borderRadius: '24px',
                        }}>
                          <span style={{
                            position: 'absolute',
                            content: '""',
                            height: '16px',
                            width: '16px',
                            left: bannerEditData.muted ? '24px' : '4px',
                            bottom: '4px',
                            backgroundColor: 'white',
                            transition: '.3s',
                            borderRadius: '50%'
                          }} />
                        </span>
                      </label>
                    </div>
                    
                    {/* 반복 재생 설정 - 유지 */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '10px 12px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '4px',
                      marginBottom: '8px'
                    }}>
                      <div>
                        <div style={{ fontWeight: '500' }}>반복 재생</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                          비디오가 끝나면 처음부터 다시 재생됩니다
                        </div>
                      </div>
                      <label style={{ 
                        position: 'relative',
                        display: 'inline-block',
                        width: '44px',
                        height: '24px'
                      }}>
                        <input
                          type="checkbox"
                          checked={bannerEditData.loop}
                          onChange={(e) => setBannerEditData({...bannerEditData, loop: e.target.checked})}
                          style={{ 
                            opacity: 0,
                            width: 0,
                            height: 0
                          }}
                        />
                        <span style={{
                          position: 'absolute',
                          cursor: 'pointer',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: bannerEditData.loop ? '#0ea5e9' : '#cbd5e1',
                          transition: '.3s',
                          borderRadius: '24px',
                        }}>
                          <span style={{
                            position: 'absolute',
                            content: '""',
                            height: '16px',
                            width: '16px',
                            left: bannerEditData.loop ? '24px' : '4px',
                            bottom: '4px',
                            backgroundColor: 'white',
                            transition: '.3s',
                            borderRadius: '50%'
                          }} />
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
              
              {/* 공통 배너 속성 */}
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>배너 제목</label>
                <input 
                  type="text" 
                  value={bannerEditData.title}
                  onChange={(e) => setBannerEditData({...bannerEditData, title: e.target.value})}
                  placeholder="배너 제목을 입력하세요"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>
              
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>배너 설명</label>
                <textarea 
                  value={bannerEditData.description}
                  onChange={(e) => setBannerEditData({...bannerEditData, description: e.target.value})}
                  placeholder="배너 설명을 입력하세요"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    minHeight: '80px',
                    resize: 'vertical'
                  }}
                />
              </div>
              
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>배너 링크</label>
                <input 
                  type="text" 
                  value={bannerEditData.url}
                  onChange={(e) => setBannerEditData({...bannerEditData, url: e.target.value})}
                  placeholder="배너 클릭 시 이동할 URL"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>
              
              {/* 배너 내용 위치 설정 그리드 UI */}
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>배너 내용 위치</label>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(3, 1fr)', 
                  gap: '8px',
                  marginTop: '10px'
                }}>
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(pos => (
                    <div 
                      key={pos}
                      onClick={() => setBannerEditData({...bannerEditData, position: pos})}
                      style={{
                        width: '100%',
                        aspectRatio: '1/1',
                        border: `2px solid ${bannerEditData.position === pos ? '#0ea5e9' : '#ddd'}`,
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        backgroundColor: bannerEditData.position === pos ? '#f0f9ff' : 'white',
                      }}
                    >
                      {getPosLabel(pos)}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* 배경 활성화 옵션 */}
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  backgroundColor: '#f8fafc',
                  padding: '12px 16px',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0'
                }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      fontWeight: 'bold', 
                      marginBottom: '4px' 
                    }}>
                      배경 활성화
                    </label>
                    <span style={{ fontSize: '13px', color: '#64748b' }}>
                      배너 내용에 반투명 배경을 추가합니다
                    </span>
                  </div>
                  <label style={{ 
                    position: 'relative',
                    display: 'inline-block',
                    width: '44px',
                    height: '24px'
                  }}>
                    <input
                      type="checkbox"
                      checked={bannerEditData.hasBackground}
                      onChange={(e) => setBannerEditData({...bannerEditData, hasBackground: e.target.checked})}
                      style={{ 
                        opacity: 0,
                        width: 0,
                        height: 0
                      }}
                    />
                    <span style={{
                      position: 'absolute',
                      cursor: 'pointer',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: bannerEditData.hasBackground ? '#0ea5e9' : '#cbd5e1',
                      transition: '.3s',
                      borderRadius: '24px',
                      '&:before': {
                        position: 'absolute',
                        content: '""',
                        height: '16px',
                        width: '16px',
                        left: bannerEditData.hasBackground ? '24px' : '4px',
                        bottom: '4px',
                        backgroundColor: 'white',
                        transition: '.3s',
                        borderRadius: '50%'
                      }
                    }}>
                      <span style={{
                        position: 'absolute',
                        content: '""',
                        height: '16px',
                        width: '16px',
                        left: bannerEditData.hasBackground ? '24px' : '4px',
                        bottom: '4px',
                        backgroundColor: 'white',
                        transition: '.3s',
                        borderRadius: '50%'
                      }} />
                    </span>
                  </label>
                </div>
              </div>
              
              {/* 텍스트 색상 및 스타일 섹션 */}
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <div style={{
                  backgroundColor: '#f8fafc',
                  padding: '16px',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0'
                }}>
                  <h4 style={{ 
                    fontSize: '16px', 
                    fontWeight: 'bold', 
                    marginTop: '0', 
                    marginBottom: '16px' 
                  }}>
                    텍스트 스타일 설정
                  </h4>
                  
                  {/* 텍스트 색상 */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      텍스트 색상
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '4px',
                        backgroundColor: bannerEditData.textColor,
                        border: '1px solid #e2e8f0'
                      }} />
                      <input
                        type="color"
                        value={bannerEditData.textColor}
                        onChange={(e) => setBannerEditData({...bannerEditData, textColor: e.target.value})}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* 버튼 스타일 섹션 */}
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <div style={{
                  backgroundColor: '#f8fafc',
                  padding: '16px',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0'
                }}>
                  <h4 style={{ 
                    fontSize: '16px', 
                    fontWeight: 'bold', 
                    marginTop: '0', 
                    marginBottom: '16px' 
                  }}>
                    버튼 스타일 설정
                  </h4>
                  
                  {/* 버튼 텍스트 */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      버튼 텍스트
                    </label>
                    <input
                      type="text"
                      value={bannerEditData.buttonText}
                      onChange={(e) => setBannerEditData({...bannerEditData, buttonText: e.target.value})}
                      placeholder="버튼에 표시할 텍스트"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        border: '1px solid #e2e8f0'
                      }}
                    />
                  </div>
                  
                  {/* 투명 배경 설정 */}
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      backgroundColor: '#f1f5f9',
                      padding: '12px 16px',
                      borderRadius: '6px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <div>
                        <label style={{ 
                          display: 'block', 
                          fontWeight: 'bold', 
                          marginBottom: '4px' 
                        }}>
                          투명 배경
                        </label>
                        <span style={{ fontSize: '13px', color: '#64748b' }}>
                          버튼 배경을 투명하게 만들고 테두리를 추가합니다
                        </span>
                      </div>
                      <input
                        type="checkbox"
                        checked={bannerEditData.transparentButton}
                        onChange={(e) => setBannerEditData({...bannerEditData, transparentButton: e.target.checked})}
                      />
                    </div>
                  </div>
                  
                  {/* 버튼 크기 설정 */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      버튼 크기 설정 (PC)
                    </label>
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>너비</label>
                        <input
                          type="text"
                          value={bannerEditData.buttonWidth}
                          onChange={(e) => setBannerEditData({...bannerEditData, buttonWidth: e.target.value})}
                          placeholder="auto, 100px, 50% 등"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            border: '1px solid #e2e8f0',
                            fontSize: '13px'
                          }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>높이</label>
                        <input
                          type="text"
                          value={bannerEditData.buttonHeight}
                          onChange={(e) => setBannerEditData({...bannerEditData, buttonHeight: e.target.value})}
                          placeholder="auto, 40px 등"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            border: '1px solid #e2e8f0',
                            fontSize: '13px'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* 버튼 미리보기 */}
                  <div style={{ 
                    marginBottom: '16px', 
                    display: 'flex',
                    justifyContent: 'center' 
                  }}>
                    <div style={{
                      backgroundColor: bannerEditData.transparentButton ? 'transparent' : bannerEditData.buttonBgColor,
                      color: bannerEditData.buttonTextColor,
                      padding: '10px 20px',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      fontWeight: 'bold',
                      boxShadow: bannerEditData.transparentButton ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.1)',
                      border: bannerEditData.transparentButton ? `1px solid ${bannerEditData.buttonTextColor}` : 'none',
                      width: bannerEditData.buttonWidth !== 'auto' ? bannerEditData.buttonWidth : 'auto',
                      height: bannerEditData.buttonHeight !== 'auto' ? bannerEditData.buttonHeight : 'auto'
                    }}>
                      {bannerEditData.buttonText || '자세히 보기'} 
                    </div>
                  </div>
                  
                  {/* 버튼 배경색 */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      버튼 배경색
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '4px',
                        backgroundColor: bannerEditData.buttonBgColor,
                        border: '1px solid #e2e8f0'
                      }} />
                      <input
                        type="color"
                        value={bannerEditData.buttonBgColor}
                        onChange={(e) => setBannerEditData({...bannerEditData, buttonBgColor: e.target.value})}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                  
                  {/* 버튼 텍스트 색상 */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      버튼 텍스트 색상
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '4px',
                        backgroundColor: bannerEditData.buttonTextColor,
                        border: '1px solid #e2e8f0'
                      }} />
                      <input
                        type="color"
                        value={bannerEditData.buttonTextColor}
                        onChange={(e) => setBannerEditData({...bannerEditData, buttonTextColor: e.target.value})}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                  
                  {/* 버튼 호버 색상 */}
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      호버 시 배경색
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '4px',
                        backgroundColor: bannerEditData.buttonHoverColor,
                        border: '1px solid #e2e8f0'
                      }} />
                      <input
                        type="color"
                        value={bannerEditData.buttonHoverColor}
                        onChange={(e) => setBannerEditData({...bannerEditData, buttonHoverColor: e.target.value})}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-footer" style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              marginTop: '16px'
            }}>
              <button 
                onClick={() => setShowBannerEdit(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                취소
              </button>
              <button 
                onClick={handleSaveBanner}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#0ea5e9',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* 공통 속성 컴포넌트 사용 */}
      {/* <PaddingControl />
      <BorderRadiusControl />
      <StyleControl /> */}
    </>
  );
};

export default MainBannerProperties; 